export interface AppWidgetItem {
  id: string
  name: string
  description?: string
  icon?: string
  category?: string
  categories?: string[]
  type: 'app' | 'widget'
  [key: string]: any
}

export type SelectedAppsWidgets = {
  apps: AppWidgetItem[]
  widgets: AppWidgetItem[]
}

// Default widget catalogue surfaced in the modal when no widgets prop is supplied.
export const defaultWidgetCatalog: AppWidgetItem[] = [
  {
    id: 'widget-dividend-calendar',
    name: 'Dividend calendar',
    description: 'Upcoming dividend events across your covered issuers.',
    category: 'Markets',
    iconName: 'calendar',
    type: 'widget'
  },
  {
    id: 'widget-most-used',
    name: 'Most used features',
    description: 'Quick access to the tools you open most often.',
    category: 'Productivity',
    iconName: 'star',
    type: 'widget'
  },
  {
    id: 'widget-climate-score',
    name: 'Climate score',
    description: 'Issuer level climate score summary at a glance.',
    category: 'ESG',
    iconName: 'leaf',
    type: 'widget'
  },
  {
    id: 'widget-watchlist',
    name: 'Watchlist',
    description: 'Track the instruments and portfolios you care about.',
    category: 'Markets',
    iconName: 'eye',
    type: 'widget'
  }
]

// Extract the human readable category names an application belongs to. The
// search API returns them under `applicationInCategories` as an array of
// `{ category: { name } }` objects; older/other shapes use a `categories`
// array or a single `category` string, so all are handled here.
const extractCategoryNames = (application: any): string[] => {
  const fromRelation = Array.isArray(application?.applicationInCategories)
    ? application.applicationInCategories.map(
        (relation: any) => relation?.category?.name ?? relation?.categoryName ?? relation?.name
      )
    : []
  const fromArray = Array.isArray(application?.categories)
    ? application.categories.map((category: any) =>
        typeof category === 'string' ? category : category?.name
      )
    : []
  const single = application?.category ?? application?.categoryName
  const names = [...fromRelation, ...fromArray, ...(single ? [single] : [])]
  return Array.from(
    new Set(names.filter((name: any): name is string => Boolean(name && String(name).trim())))
  )
}

// Normalise an application object returned by the search API into an AppWidgetItem.
export const mapApplicationToItem = (application: any): AppWidgetItem => {
  const categoryNames = extractCategoryNames(application)
  return {
    ...application,
    id: String(application?.id ?? application?.applicationId ?? application?.name),
    name: application?.name ?? '',
    description: application?.shortDescription ?? application?.description ?? '',
    icon: application?.appIcon?.url,
    category: categoryNames[0] ?? 'Uncategorised',
    categories: categoryNames,
    type: 'app'
  }
}

// Build the ordered, de-duplicated list of category pills for the supplied items.
export const buildCategories = (items: AppWidgetItem[]): string[] => {
  const categories = items
    .map((item) => item.category)
    .filter((category): category is string => Boolean(category))
  return ['All', ...Array.from(new Set(categories))]
}

// Build the ordered, de-duplicated category pills from the search API `categories` array.
export const buildCategoriesFromApi = (categories: any[]): string[] => {
  const names = (categories ?? [])
    .map((category) => category?.name)
    .filter((name): name is string => Boolean(name && name.trim()))
  return ['All', ...Array.from(new Set(names))]
}

// Filter items by the active tab, search term and selected category.
export const filterItems = (
  items: AppWidgetItem[],
  { searchTerm, category }: { searchTerm: string; category: string }
): AppWidgetItem[] => {
  const term = searchTerm.trim().toLowerCase()
  return items.filter((item) => {
    const itemCategories = Array.isArray(item.categories)
      ? item.categories
      : item.category
      ? [item.category]
      : []
    const matchesCategory = category === 'All' || itemCategories.includes(category)
    if (!matchesCategory) return false
    if (!term) return true
    return (
      item.name.toLowerCase().includes(term) ||
      (item.description ?? '').toLowerCase().includes(term)
    )
  })
}

// Human readable summary of the current selection, e.g. "2 apps and 5 widgets selected".
export const buildSelectionSummary = (appCount: number, widgetCount: number): string => {
  const appLabel = `${appCount} app${appCount === 1 ? '' : 's'}`
  const widgetLabel = `${widgetCount} widget${widgetCount === 1 ? '' : 's'}`
  return `${appLabel} and ${widgetLabel} selected`
}
